import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { Loader2, ArrowLeft, Check } from 'lucide-react';

const forgotPasswordSchema = z.object({
    email: z.string().email('Email inválido'),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
    const { resetPassword } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<ForgotPasswordForm>({
        resolver: zodResolver(forgotPasswordSchema),
    });

    const onSubmit = async (data: ForgotPasswordForm) => {
        try {
            setError(null);
            await resetPassword(data.email);
            setSuccess(true);
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message || 'Erro ao enviar email');
            } else {
                setError('Erro ao enviar email');
            }
        }
    };

    if (success) {
        return (
            <div className="animate-fade-in text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Email enviado!
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                </p>
                <Link to="/login" className="btn-primary">
                    Voltar para login
                </Link>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
            >
                <ArrowLeft className="w-4 h-4" />
                Voltar para login
            </Link>

            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Esqueceu sua senha?
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Sem problemas! Informe seu email e enviaremos instruções para redefinir sua senha.
                </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {error && (
                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                )}

                <div>
                    <label htmlFor="email" className="label">
                        Email
                    </label>
                    <input
                        {...register('email')}
                        type="email"
                        id="email"
                        autoComplete="email"
                        className={`input ${errors.email ? 'input-error' : ''}`}
                        placeholder="seu@email.com"
                    />
                    {errors.email && (
                        <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Enviando...
                        </>
                    ) : (
                        'Enviar instruções'
                    )}
                </button>
            </form>
        </div>
    );
}
